// file: src/components/viewers/SpreadsheetViewer.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { FixedSizeList as List } from 'react-window';
import { useFileDataCache } from '../../hooks/useFileDataCache';
import styles from './SpreadsheetViewer.module.css';

function SpreadsheetViewer({ file }) {
  const { fileData, loading, error } = useFileDataCache(file.id);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState(null);
  const [parseComplete, setParseComplete] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [columnWidths, setColumnWidths] = useState([]);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const headerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  
  const rowDataRef = useRef([]);

  // Calculate initial column widths based on content
  const calculateColumnWidths = useCallback((data) => {
    if (!data || !data.length) return [];
    
    // Get headers first to determine column count
    const firstRow = data && data.length > 0 ? data[0] : {};
    const headers = Object.keys(firstRow || {});
    const columnCount = headers.length;
    const widths = new Array(columnCount).fill(150); // Increased minimum width
    
    // Sample first 100 rows for width calculation
    const sampleSize = Math.min(100, data.length);
    
    // First calculate header widths
    headers.forEach((header, colIndex) => {
      const headerWidth = (header?.toString() || '').length * 10 + 32; // More space for headers
      widths[colIndex] = Math.max(widths[colIndex], headerWidth);
    });
    
    // Then calculate data widths
    for (let i = 0; i < sampleSize; i++) {
      const row = data[i];
      if (!row || typeof row !== 'object') continue;
      
      headers.forEach((header, colIndex) => {
        const cell = row[header];
        const cellContent = cell?.toString() || '';
        const cellWidth = Math.min(400, cellContent.length * 8 + 32); // Better width calculation
        widths[colIndex] = Math.max(widths[colIndex], cellWidth);
      });
    }
    
    return widths;
  }, []);

  // Sync horizontal scroll between header and body
  const handleScroll = useCallback((e) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = e.target.scrollLeft;
      setScrollLeft(e.target.scrollLeft);
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    if (!fileData) return;
    
    try {
      const text = new TextDecoder().decode(fileData);
      rowDataRef.current = [];
      setParseError(null);

      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        chunk: (results, parser) => {
          if (results.errors?.length) {
            console.error('Chunk parsing errors:', results.errors);
            setParseError(results.errors && results.errors.length > 0 ? results.errors[0].message : 'Unknown parse error');
            parser.abort();
            return;
          }

          if (Array.isArray(results.data)) {
            const validData = results.data.filter(row => row && typeof row === 'object');
            rowDataRef.current = rowDataRef.current.concat(validData);
            setRows([...rowDataRef.current]);
            
            if (!headers && results.meta?.fields?.length) {
              setHeaders(results.meta.fields);
              const initialWidths = calculateColumnWidths(validData);
              setColumnWidths(initialWidths);
            }
          }
        },
        complete: () => {
          setParseComplete(true);
        },
        error: (err) => {
          console.error('Papa Parse error:', err);
          setParseError(err.message || 'Failed to parse CSV file');
        },
      });
    } catch (err) {
      console.error('CSV parsing error:', err);
      setParseError(err.message || 'Failed to process CSV file');
    }
  }, [fileData, calculateColumnWidths, headers]);

  if (loading) {
    return <div className={styles.infoMessage}>Loading CSV data...</div>;
  }
  
  if (error) {
    return <div className={styles.errorMessage}>Error loading CSV: {error.message}</div>;
  }

  if (parseError) {
    return <div className={styles.errorMessage}>{parseError}</div>;
  }

  if (!rows.length && !parseComplete) {
    return <div className={styles.infoMessage}>Parsing CSV data...</div>;
  }

  if (!rows.length && parseComplete) {
    return <div className={styles.infoMessage}>No data found in CSV file.</div>;
  }

  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);

  const RowRenderer = ({ index, style }) => {
    const row = rows[index];
    if (!row || typeof row !== 'object') return null;

    return (
      <div 
        className={`${styles.row} ${index % 2 === 0 ? styles.evenRow : ''}`} 
        style={{
          ...style,
          width: totalWidth,
          minWidth: '100%',
        }}
      >
        {headers.map((header, colIndex) => {
          const cellContent = row[header]?.toString() || '';
          return (
            <div
              key={colIndex}
              className={styles.cell}
              style={{
                width: columnWidths[colIndex],
                minWidth: columnWidths[colIndex],
              }}
              title={cellContent} // Show full content on hover
            >
              {cellContent}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.tableContainer}>
        {/* Sticky header */}
        <div className={styles.headerWrapper} ref={headerRef}>
          <div 
            className={styles.headerRow} 
            style={{ 
              width: totalWidth,
              minWidth: '100%',
              transform: `translateX(-${scrollLeft}px)` 
            }}
          >
            {headers && headers.map((header, colIndex) => (
              <div
                key={colIndex}
                className={styles.headerCell}
                style={{
                  width: columnWidths[colIndex],
                  minWidth: columnWidths[colIndex],
                }}
                title={header} // Show full header on hover
              >
                {header}
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable content area */}
        <div className={styles.scrollContainer} ref={scrollContainerRef}>
          <List
            ref={listRef}
            height={Math.min(800, window.innerHeight - 200)}
            itemCount={rows.length}
            itemSize={36} // Slightly increased row height
            width="100%"
            overscanCount={5}
            className={styles.virtualList}
          >
            {RowRenderer}
          </List>
        </div>
      </div>

      {!parseComplete && (
        <div className={styles.infoMessage}>
          <div className={styles.loadingSpinner} />
          Processing data...
        </div>
      )}

      {/* Row count indicator */}
      <div className={styles.statusBar}>
        {parseComplete ? (
          `${rows.length.toLocaleString()} rows`
        ) : (
          `Loaded ${rows.length.toLocaleString()} rows...`
        )}
      </div>
    </div>
  );
}

export default SpreadsheetViewer;
